export type UserEvent =
  | "downloadCompleted"
  | "fullScreenAnnouncement"
  | "newMessage"
  | "pushNotification"
  | "unlockedAchievement"
  | "userProfileUpdated";

export type RoomType = "idea" | "projectBoard";

export type RoomRef = { type: RoomType; id: string };

export type SocketUser = { _id: string; profile: Record<string, any> };

// Assigned by the server: participants are visible to other members and may
// signal, observers receive room events silently
export type RoomRole = "participant" | "observer";

export type RoomState = {
  role: RoomRole | null;
  // Visible members, only for room types with presence
  members: SocketUser[];
};

export type RoomMessage = {
  event: string;
  user: SocketUser;
  toUser?: string;
  [key: string]: any;
};

export type RoomHandle = {
  room: RoomRef;
  leave: () => void;
  // Re-join so that the server re-evaluates the role, e.g. after becoming a collaborator
  rejoin: () => void;
};

export type WebsocketStatus =
  | "idle"
  | "connecting"
  | "connected"
  | "disconnected";

type WebsocketAPIOptions = {
  websocketUrl: string;
  generateToken: () => Promise<string | null>;
};

type RoomListener = {
  onMessage: (message: RoomMessage) => void;
  onStateChange?: (state: RoomState) => void;
};

type RoomEntry = {
  room: RoomRef;
  listeners: Set<RoomListener>;
  joined: boolean;
  state: RoomState;
  leaveTimeout?: ReturnType<typeof setTimeout>;
};

// API Gateway closes idle connections after 10 minutes and all connections after 2 hours
const HEARTBEAT_INTERVAL = 5 * 60 * 1000;
const MAX_CONNECTION_AGE = 110 * 60 * 1000;
const MAX_RETRY_DELAY = 30 * 1000;
// Connections dropped sooner than this keep backing off, so open-then-drop cycles don't reconnect in a tight loop
const STABLE_CONNECTION_AGE = 30 * 1000;
const LEAVE_DELAY = 1000;

export const roomKey = (room: RoomRef) => `${room.type}:${room.id}`;

export const EMPTY_ROOM_STATE: RoomState = { role: null, members: [] };

const safely = (fn: () => void) => {
  try {
    fn();
  } catch (error) {
    console.error(error);
  }
};

export class WebsocketAPI {
  status: WebsocketStatus = "idle";
  options: WebsocketAPIOptions;

  private socket: WebSocket | undefined;
  private shouldConnect = false;
  private isFetchingToken = false;
  private hasConnected = false;
  private retries = 0;
  private seq = 0;
  private retryTimeout?: ReturnType<typeof setTimeout>;
  private heartbeatInterval?: ReturnType<typeof setInterval>;
  private maxAgeTimeout?: ReturnType<typeof setTimeout>;
  private stableTimeout?: ReturnType<typeof setTimeout>;
  private rooms = new Map<string, RoomEntry>();
  private eventListeners = new Map<string, Set<(payload: any) => void>>();
  private statusListeners = new Set<(status: WebsocketStatus) => void>();
  private reconnectListeners = new Set<() => void>();

  constructor(options: WebsocketAPIOptions) {
    this.options = options;

    if (typeof window !== "undefined" && window.addEventListener) {
      window.addEventListener("online", () => this.retryNow());
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") this.retryNow();
      });
    }
  }

  connect() {
    this.shouldConnect = true;
    if (this.socket || this.isFetchingToken) return;
    clearTimeout(this.retryTimeout);
    this.open();
  }

  disconnect() {
    this.shouldConnect = false;
    this.hasConnected = false;
    this.retries = 0;
    clearTimeout(this.retryTimeout);
    this.stopTimers();

    const socket = this.socket;
    this.socket = undefined;
    socket?.close(1000);
    this.resetRooms();
    this.setStatus("idle");
  }

  // Skip any pending backoff, e.g. when the device comes back online or the app is foregrounded
  retryNow() {
    if (!this.shouldConnect) return;
    if (this.socket) {
      if (this.socket.readyState === WebSocket.OPEN) {
        this.send("heartbeat", {});
      }
      return;
    }
    if (this.isFetchingToken) return;
    this.retries = 0;
    clearTimeout(this.retryTimeout);
    this.open();
  }

  on<T = any>(event: UserEvent, handler: (payload: T) => void) {
    let listeners = this.eventListeners.get(event);
    if (!listeners) {
      listeners = new Set();
      this.eventListeners.set(event, listeners);
    }
    listeners.add(handler);
    return () => {
      listeners.delete(handler);
    };
  }

  joinRoom(
    room: RoomRef,
    onMessage: (message: RoomMessage) => void,
    onStateChange?: (state: RoomState) => void,
  ): RoomHandle {
    const key = roomKey(room);
    let entry = this.rooms.get(key);
    if (!entry) {
      entry = {
        room,
        listeners: new Set(),
        joined: false,
        state: EMPTY_ROOM_STATE,
      };
      this.rooms.set(key, entry);
    }
    clearTimeout(entry.leaveTimeout);

    const roomEntry = entry;
    const listener: RoomListener = { onMessage, onStateChange };
    roomEntry.listeners.add(listener);
    if (roomEntry.state.role) onStateChange?.(roomEntry.state);
    this.syncRoom(roomEntry);

    return {
      room,
      leave: () => {
        if (!roomEntry.listeners.delete(listener) || roomEntry.listeners.size)
          return;
        // Delay leaving so that quickly re-joining (e.g. on re-render) does not churn presence
        roomEntry.leaveTimeout = setTimeout(() => {
          if (roomEntry.listeners.size || this.rooms.get(key) !== roomEntry)
            return;
          this.rooms.delete(key);
          if (roomEntry.joined) {
            this.send("leave", { room, seq: ++this.seq });
          }
        }, LEAVE_DELAY);
      },
      rejoin: () => {
        if (!roomEntry.joined || !roomEntry.listeners.has(listener)) return;
        roomEntry.joined = false;
        this.syncRoom(roomEntry);
      },
    };
  }

  // Sends an event to the other members of a joined room; ignored unless we are a participant
  signal(
    room: RoomRef,
    event: string,
    data: Record<string, unknown> = {},
    toUser?: string,
  ) {
    if (this.rooms.get(roomKey(room))?.state.role !== "participant") return;
    this.send("signal", { room, event, data, toUser });
  }

  onStatusChange(handler: (status: WebsocketStatus) => void) {
    this.statusListeners.add(handler);
    return () => {
      this.statusListeners.delete(handler);
    };
  }

  // Called after the connection was re-established; anything sent in the meantime was missed
  onReconnect(handler: () => void) {
    this.reconnectListeners.add(handler);
    return () => {
      this.reconnectListeners.delete(handler);
    };
  }

  private async open() {
    if (!this.options.websocketUrl) return;

    if (this.status === "idle") this.setStatus("connecting");
    this.isFetchingToken = true;
    let token: string | null = null;
    try {
      token = await this.options.generateToken();
    } catch {
      token = null;
    }
    this.isFetchingToken = false;

    if (!this.shouldConnect || this.socket) return;
    if (!token) {
      this.scheduleRetry();
      return;
    }

    const socket = new WebSocket(
      `${this.options.websocketUrl}?token=${encodeURIComponent(token)}`,
    );
    this.socket = socket;

    socket.onopen = () => {
      if (this.socket === socket) this.handleOpen();
    };
    socket.onmessage = (event) => {
      if (this.socket === socket) this.handleMessage(event);
    };
    socket.onclose = () => {
      if (this.socket !== socket) return;
      this.socket = undefined;
      this.handleClose();
    };
  }

  private handleOpen() {
    const isReconnect = this.hasConnected;
    this.hasConnected = true;
    this.setStatus("connected");

    this.stableTimeout = setTimeout(() => {
      this.retries = 0;
    }, STABLE_CONNECTION_AGE);

    this.heartbeatInterval = setInterval(
      () => this.send("heartbeat", {}),
      HEARTBEAT_INTERVAL,
    );
    // Recycle the connection before API Gateway forcefully closes it
    this.maxAgeTimeout = setTimeout(
      () => this.socket?.close(4000),
      MAX_CONNECTION_AGE,
    );

    this.rooms.forEach((entry, key) => {
      if (entry.listeners.size) {
        this.syncRoom(entry);
      } else {
        clearTimeout(entry.leaveTimeout);
        this.rooms.delete(key);
      }
    });

    if (isReconnect) {
      this.reconnectListeners.forEach((handler) => safely(handler));
    }
  }

  private handleMessage(event: MessageEvent) {
    let message: { type?: string; room?: string; payload?: any };
    try {
      message = JSON.parse(event.data);
    } catch {
      return;
    }
    const { type, room, payload = {} } = message ?? {};
    if (typeof type !== "string") return;

    if (room) {
      const entry = this.rooms.get(room);
      if (!entry) return;
      if (type === "__joined") {
        this.setRoomState(entry, {
          role: payload.role ?? null,
          members: payload.members ?? [],
        });
      } else if (type === "__members") {
        this.setRoomState(entry, {
          ...entry.state,
          members: payload.members ?? [],
        });
      } else {
        entry.listeners.forEach((listener) =>
          safely(() => listener.onMessage({ ...payload, event: type })),
        );
      }
    } else {
      this.eventListeners
        .get(type)
        ?.forEach((handler) => safely(() => handler(payload)));
    }
  }

  private handleClose() {
    this.stopTimers();
    this.resetRooms();
    if (!this.shouldConnect) {
      this.setStatus("idle");
      return;
    }
    this.scheduleRetry();
  }

  private scheduleRetry() {
    // Retry immediately once, then back off exponentially with jitter
    const backoff =
      this.retries === 0
        ? 0
        : Math.min(MAX_RETRY_DELAY, 1000 * 2 ** (this.retries - 1));
    const delay = backoff * (0.5 + Math.random() / 2);
    this.retries += 1;
    this.setStatus(this.retries > 1 ? "disconnected" : "connecting");

    clearTimeout(this.retryTimeout);
    this.retryTimeout = setTimeout(() => {
      if (this.shouldConnect && !this.socket && !this.isFetchingToken) {
        this.open();
      }
    }, delay);
  }

  private syncRoom(entry: RoomEntry) {
    if (entry.joined || this.socket?.readyState !== WebSocket.OPEN) return;
    entry.joined = true;
    this.send("join", { room: entry.room, seq: ++this.seq });
  }

  private setRoomState(entry: RoomEntry, state: RoomState) {
    entry.state = state;
    entry.listeners.forEach((listener) =>
      safely(() => listener.onStateChange?.(state)),
    );
  }

  // Room state is kept while disconnected and replaced once the room is re-joined
  private resetRooms() {
    this.rooms.forEach((entry, key) => {
      entry.joined = false;
      if (!entry.listeners.size) {
        clearTimeout(entry.leaveTimeout);
        this.rooms.delete(key);
      }
    });
  }

  private stopTimers() {
    clearInterval(this.heartbeatInterval);
    clearTimeout(this.maxAgeTimeout);
    clearTimeout(this.stableTimeout);
  }

  private setStatus(status: WebsocketStatus) {
    if (this.status === status) return;
    this.status = status;
    this.statusListeners.forEach((handler) => safely(() => handler(status)));
  }

  private send(action: string, data: Record<string, unknown>) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ action, ...data }));
    }
  }
}
